import {useMemo} from "react";
import {css, cx} from "@panda/css";

import {Building3DViewIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {RequestState} from "@pg-mono/hooks";
import {SkeletonBox} from "@pg-mono/skeleton-box";

import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {hasOffer3dView} from "../../utils/has_offer_3d_view";
import {GalleryHolderMap} from "./GalleryHolderMap";

const building3dViewImage = require("../../images/building3Dview.png");
const presaleOfferDefaultImg375x212 = require("../../images/presale_offer_default_img_375x212.jpg");

interface IProps {
    offer: IPropertyDetailOffer | IOfferDetail;
    planImage: string | null | undefined;
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    planImageAltText: string;
    galleryPictures?: IOfferGallery | null;
    galleryFetchState: RequestState;
    propertyGalleryPictures?: IPropertyGallery | null;
    externalPlan?: string | null;
    displayOnlyOneColumn?: boolean;
}

export const GalleryHolderRightSection = (props: IProps) => {
    const {externalPlan, galleryPictures, galleryFetchState, offer, propertyGalleryPictures, planImage, handleGalleryModalChange, planImageAltText} = props;

    const isFirstPropertyGalleryImageAvailable = () => {
        return !!(propertyGalleryPictures && propertyGalleryPictures.length > 0 && propertyGalleryPictures[0]?.image);
    };

    const has3dView = hasOffer3dView(offer);

    const building3dViewTile = (aspectRatio: string) => (
        <div
            role="button"
            tabIndex={0}
            className={cx(building3dViewTileWrapper, css({aspectRatio: aspectRatio}))}
            onClick={() => handleGalleryModalChange(OfferModalActiveTab.CONSTRUCTION_GALLERY)}
            data-gtm-click="Zobacz zdjęcia z budowy"
        >
            <img src={building3dViewImage} alt="" className={building3dViewImageStyle} loading="lazy" />
            <div className={building3dViewIconOverlay}>
                <Building3DViewIcon fill="white" size="6" />
            </div>
        </div>
    );

    const firstOfferGalleryImage = has3dView ? (
        building3dViewTile("760/428")
    ) : (
        <Image
            src={galleryPictures?.[0]?.image?.g_img_760x428 || ""}
            alt={offer.name}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.GALLERY);
            }}
            ratio={{
                xs: "760/428"
            }}
        />
    );

    const firstPropertyGalleryImage = has3dView ? (
        building3dViewTile("760/428")
    ) : (
        <Image
            src={propertyGalleryPictures?.[0]?.image?.g_img_155x87 || ""}
            alt={offer.name}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.GALLERY);
            }}
            ratio={{
                xs: "760/428"
            }}
        />
    );

    const propertyPlanImage = (
        <Image
            src={externalPlan || planImage || ""}
            alt={planImageAltText}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.PLAN);
            }}
            ratio={{
                xs: "760/428"
            }}
            imageStyle={{height: "100%", objectFit: "contain"}}
            className={propertyPlanImageStyles}
        />
    );

    const firstGalleryImage = isFirstPropertyGalleryImageAvailable() ? firstPropertyGalleryImage : firstOfferGalleryImage;
    const galleryImages = galleryPictures?.map((item) => item && item.image && item.image.g_img_372x210).filter(Boolean) as string[];

    const imagesArray = useMemo(() => {
        if (!galleryPictures) {
            return placeholders;
        }
        if (galleryPictures) {
            return galleryImages;
        }
        return [];
    }, [props.offer.id, galleryPictures]);

    const galleryImgSmallArray = galleryPictures ? imagesArray : placeholders;
    const thumbsCount = props.displayOnlyOneColumn ? 2 : 4;
    const thumbsDisplayArray = has3dView ? [null, ...galleryImgSmallArray].slice(0, thumbsCount) : galleryImgSmallArray.slice(0, thumbsCount);

    const thumbsGalleryHolder = (
        <div className={props.displayOnlyOneColumn ? thumbsGalleryOneColumnHolderStyle : thumbsGalleryHolderStyle}>
            {thumbsDisplayArray.map((item, idx) => {
                const isFirstAnd3dView = has3dView && idx === 0;
                const imageSrc = has3dView && idx > 0 ? galleryImgSmallArray[idx - 1] : item;
                return (
                    (isFirstAnd3dView || imageSrc) && (
                        <div
                            data-gtm-click={isFirstAnd3dView ? "Zobacz zdjęcia z budowy" : "Zobacz galerię"}
                            key={idx}
                            onClick={isFirstAnd3dView ? undefined : () => handleGalleryModalChange(OfferModalActiveTab.GALLERY)}
                        >
                            {galleryFetchState === RequestState.None || galleryFetchState === RequestState.Waiting ? (
                                <div className={skeletonWrapper}>
                                    <SkeletonBox height="100%" width="100%" />
                                </div>
                            ) : isFirstAnd3dView ? (
                                building3dViewTile("375/210")
                            ) : (
                                <Image
                                    src={imageSrc ?? undefined}
                                    alt={offer.name}
                                    width="375px"
                                    height={undefined}
                                    ratio={{
                                        xs: "375/210"
                                    }}
                                />
                            )}
                        </div>
                    )
                );
            })}
            {!props.displayOnlyOneColumn && (galleryPictures?.length === 0 || galleryPictures?.length === 1 || galleryPictures?.length === 2) && (
                <div data-gtm-click="Zobacz mapę" onClick={() => handleGalleryModalChange(OfferModalActiveTab.MAP)} title="Zobacz na mapie">
                    <div className={thumpMapHolder}>
                        <GalleryHolderMap src={offer.map_image} alt={offer.name} />
                    </div>
                </div>
            )}
            {!props.displayOnlyOneColumn && ((galleryPictures?.length === 2 && !has3dView) || (galleryPictures?.length === 1 && has3dView)) && (
                <div>{blankImageSmall}</div>
            )}
        </div>
    );

    const offerRightImage = galleryPictures == null ? blankImage : thumbsGalleryHolder;

    return planImage ? propertyPlanImage : isFirstPropertyGalleryImageAvailable() ? firstGalleryImage : offerRightImage;
};

const blankImage = <Image height="760" width={undefined} ratio={{xs: "752/428"}} alt="" />;
const blankImageSmall = <Image height="372" width={undefined} ratio={{xs: "374/210"}} alt="" />;

const placeholder = presaleOfferDefaultImg375x212;
const placeholders = [placeholder, placeholder, placeholder, placeholder, placeholder] as string[];

const thumbsGalleryHolderStyle = css({
    display: "grid",
    gridTemplate: "1fr 1fr / 1fr 1fr",
    columnGap: 2,
    rowGap: 2
});

const thumbsGalleryOneColumnHolderStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%"
});

const skeletonWrapper = css({
    height: "100%",
    "&::after": {
        content: '""',
        display: "block",
        paddingTop: "56.25%"
    }
});

const thumpMapHolder = css({
    position: "relative",
    overflow: "hidden",
    lineHeight: 0,
    aspectRatio: "374/210"
});

const propertyPlanImageStyles = css({
    height: "100%"
});

const building3dViewTileWrapper = css({
    position: "relative",
    width: "100%",
    overflow: "hidden",
    cursor: "pointer"
});

const building3dViewImageStyle = css({
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover"
});

const building3dViewIconOverlay = css({
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#00000099"
});
import {useMemo} from "react";
import {css, cx} from "@panda/css";

import {Building3DViewIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {RequestState} from "@pg-mono/hooks";
import {SkeletonBox} from "@pg-mono/skeleton-box";

import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {hasOffer3dView} from "../../utils/has_offer_3d_view";
import {GalleryHolderMap} from "./GalleryHolderMap";

const building3dViewImage = require("../../images/building3Dview.png");
const presaleOfferDefaultImg375x212 = require("../../images/presale_offer_default_img_375x212.jpg");

interface IProps {
    offer: IPropertyDetailOffer | IOfferDetail;
    planImage: string | null | undefined;
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    planImageAltText: string;
    galleryPictures?: IOfferGallery | null;
    galleryFetchState: RequestState;
    propertyGalleryPictures?: IPropertyGallery | null;
    externalPlan?: string | null;
    displayOnlyOneColumn?: boolean;
}

export const GalleryHolderRightSection = (props: IProps) => {
    const {externalPlan, galleryPictures, galleryFetchState, offer, propertyGalleryPictures, planImage, handleGalleryModalChange, planImageAltText} = props;

    const isFirstPropertyGalleryImageAvailable = () => {
        return !!(propertyGalleryPictures && propertyGalleryPictures.length > 0 && propertyGalleryPictures[0]?.image);
    };

    const has3dView = hasOffer3dView(offer);

    const building3dViewTile = (aspectRatio: string) => (
        <div
            role="button"
            tabIndex={0}
            className={cx(building3dViewTileWrapper, css({aspectRatio: aspectRatio}))}
            onClick={() => handleGalleryModalChange(OfferModalActiveTab.CONSTRUCTION_GALLERY)}
            data-gtm-click="Zobacz zdjęcia z budowy"
        >
            <img src={building3dViewImage} alt="" className={building3dViewImageStyle} loading="lazy" />
            <div className={building3dViewIconOverlay}>
                <Building3DViewIcon fill="white" size="6" />
            </div>
        </div>
    );

    const firstOfferGalleryImage = has3dView ? (
        building3dViewTile("760/428")
    ) : (
        <Image
            src={galleryPictures?.[0]?.image?.g_img_760x428 || ""}
            alt={offer.name}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.GALLERY);
            }}
            ratio={{
                xs: "760/428"
            }}
        />
    );

    const firstPropertyGalleryImage = has3dView ? (
        building3dViewTile("760/428")
    ) : (
        <Image
            src={propertyGalleryPictures?.[0]?.image?.g_img_155x87 || ""}
            alt={offer.name}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.GALLERY);
            }}
            ratio={{
                xs: "760/428"
            }}
        />
    );

    const propertyPlanImage = (
        <Image
            src={externalPlan || planImage || ""}
            alt={planImageAltText}
            width="760px"
            height={undefined}
            onClick={() => {
                handleGalleryModalChange(OfferModalActiveTab.PLAN);
            }}
            ratio={{
                xs: "760/428"
            }}
            imageStyle={{height: "100%", objectFit: "contain"}}
            className={propertyPlanImageStyles}
        />
    );

    const firstGalleryImage = isFirstPropertyGalleryImageAvailable() ? firstPropertyGalleryImage : firstOfferGalleryImage;
    const galleryImages = galleryPictures?.map((item) => item && item.image && item.image.g_img_372x210).filter(Boolean) as string[];

    const imagesArray = useMemo(() => {
        if (!galleryPictures) {
            return placeholders;
        }
        if (galleryPictures) {
            return galleryImages;
        }
        return [];
    }, [props.offer.id, galleryPictures]);

    const galleryImgSmallArray = galleryPictures ? imagesArray : placeholders;
    const thumbsCount = props.displayOnlyOneColumn ? 2 : 4;
    const thumbsDisplayArray = has3dView ? [null, ...galleryImgSmallArray].slice(0, thumbsCount) : galleryImgSmallArray.slice(0, thumbsCount);

    const thumbsGalleryHolder = (
        <div className={props.displayOnlyOneColumn ? thumbsGalleryOneColumnHolderStyle : thumbsGalleryHolderStyle}>
            {thumbsDisplayArray.map((item, idx) => {
                const isFirstAnd3dView = has3dView && idx === 0;
                const imageSrc = has3dView && idx > 0 ? galleryImgSmallArray[idx - 1] : item;
                return (
                    (isFirstAnd3dView || imageSrc) && (
                        <div
                            data-gtm-click={isFirstAnd3dView ? "Zobacz zdjęcia z budowy" : "Zobacz galerię"}
                            key={idx}
                            onClick={isFirstAnd3dView ? undefined : () => handleGalleryModalChange(OfferModalActiveTab.GALLERY)}
                        >
                            {galleryFetchState === RequestState.None || galleryFetchState === RequestState.Waiting ? (
                                <div className={skeletonWrapper}>
                                    <SkeletonBox height="100%" width="100%" />
                                </div>
                            ) : isFirstAnd3dView ? (
                                building3dViewTile("375/210")
                            ) : (
                                <Image
                                    src={imageSrc ?? undefined}
                                    alt={offer.name}
                                    width="375px"
                                    height={undefined}
                                    ratio={{
                                        xs: "375/210"
                                    }}
                                />
                            )}
                        </div>
                    )
                );
            })}
            {!props.displayOnlyOneColumn && (galleryPictures?.length === 0 || galleryPictures?.length === 1 || galleryPictures?.length === 2) && (
                <div data-gtm-click="Zobacz mapę" onClick={() => handleGalleryModalChange(OfferModalActiveTab.MAP)} title="Zobacz na mapie">
                    <div className={thumpMapHolder}>
                        <GalleryHolderMap src={offer.map_image} alt={offer.name} />
                    </div>
                </div>
            )}
            {!props.displayOnlyOneColumn && ((galleryPictures?.length === 2 && !has3dView) || (galleryPictures?.length === 1 && has3dView)) && (
                <div>{blankImageSmall}</div>
            )}
        </div>
    );

    const offerRightImage = galleryPictures == null ? blankImage : thumbsGalleryHolder;

    return planImage ? propertyPlanImage : isFirstPropertyGalleryImageAvailable() ? firstGalleryImage : offerRightImage;
};

const blankImage = <Image height="760" width={undefined} ratio={{xs: "752/428"}} alt="" />;
const blankImageSmall = <Image height="372" width={undefined} ratio={{xs: "374/210"}} alt="" />;

const placeholder = presaleOfferDefaultImg375x212;
const placeholders = [placeholder, placeholder, placeholder, placeholder, placeholder] as string[];

const thumbsGalleryHolderStyle = css({
    display: "grid",
    gridTemplate: "1fr 1fr / 1fr 1fr",
    columnGap: 2,
    rowGap: 2
});

const thumbsGalleryOneColumnHolderStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%"
});

const skeletonWrapper = css({
    height: "100%",
    "&::after": {
        content: '""',
        display: "block",
        paddingTop: "56.25%"
    }
});

const thumpMapHolder = css({
    position: "relative",
    overflow: "hidden",
    lineHeight: 0,
    aspectRatio: "374/210"
});

const propertyPlanImageStyles = css({
    height: "100%"
});

const building3dViewTileWrapper = css({
    position: "relative",
    width: "100%",
    overflow: "hidden",
    cursor: "pointer"
});

const building3dViewImageStyle = css({
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover"
});

const building3dViewIconOverlay = css({
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#00000099"
});
